import { UserManagerSettings } from "oidc-client-ts";
import { getSentryConfig } from "@core/helpers/sentryHelper";
import { TrackingName } from "@core/drivers/tracking/tracking.types";

function getServerBaseURL(): string {
    if (BUILD_AWS) {
        return 'https://backup.archipad-services.com/';
    }

    if (BUILD_LOCAL) {
        return 'http://127.0.0.1/api/archipad/www/';
    }

    if (BUILD_LOCAL_DOCKER) {
        return 'http://127.0.0.1:62010/';
    }

    if (BUILD_PROD) {
        return 'https://api.archipad-services.com/';
    }

    return 'https://stage-api.bigsool.io/';
}

function getOAuthClientConfiguration(): Partial<UserManagerSettings> {
    if (BUILD_ANDROID) {
        if (BUILD_PROD) {
            return {
                authority: OAUTH_AUTHORITY ?? 'https://auth.archipad.com/',
                client_id: OAUTH_CLIENT ?? '53e58f94-a17a-4be2-befe-28678af23bb3',
                extraQueryParams: {
                    audience: "prod.archiweb-api prod.text-modifier-api prod.domain.archipad",
                },
            };
        }
        return {
            authority: OAUTH_AUTHORITY ?? 'https://auth.archipad.dev/',
            client_id: OAUTH_CLIENT ?? 'e4c54de0-a93a-4b33-9980-9f18cbf0ed5f',
            extraQueryParams: {
                audience: "stage.archiweb-api stage.text-modifier-api stage.domain.archipad",
            },
        };
    }
    if (BUILD_PROD) {
        return {
            authority: OAUTH_AUTHORITY ?? 'https://auth.archipad.com/',
            client_id: OAUTH_CLIENT ?? '945987ff-c181-48b4-9c34-c32ba8076d1f',
            extraQueryParams: {
                audience: "prod.archiweb-api prod.text-modifier-api prod.domain.archipad",
            },
        };
    }

    return {
        authority: OAUTH_AUTHORITY ?? 'https://auth.archipad.dev/',
        client_id: OAUTH_CLIENT ?? 'a32b399b-d6d5-447c-9705-ed0887e1c0c8',
        extraQueryParams: {
            audience: "stage.archiweb-api stage.text-modifier-api stage.domain.archipad",
        },
    };
}

function getAdminApiURI(): string {
    // TODO: Update this uri once the company-legal-information is moved to the admin-api
    if (BUILD_PROD) {
        return "https://api.auth.archipad.com/";
    }

    return "https://api.auth.archipad.dev/";
}

function getProfileURI(): string {
    if (BUILD_PROD){
        return "https://auth.archipad.com/";
    }
    return PROFILE_URI || "https://auth.archipad.dev/";
}

function getBucket(): string {
    if (BUILD_LOCAL || BUILD_LOCAL_DOCKER) {
        return "dev-archipad-services-us";
    }

    return "stage-archipad-services-fr";
}

function getTextModifierAPIBaseURL(): string {
    if (BUILD_PROD) {
        return 'https://api.text-modifier.archipad.com';
    }

    return 'https://api.text-modifier.archipad.dev';
}

function getSegmentationAPIBaseURL(): string {
    if (BUILD_LOCAL_SEGMENTATION) {
        return 'https://segmentation-api.xxx.archipad.ngrok.dev';
    }

    if (BUILD_PROD) {
        return 'https://api.segmentation.archipad.com';
    }

    return 'https://api.segmentation.archipad.dev';
}

function getArchipadAPIBaseURL(): string {
    if (BUILD_PROD) {
        return 'https://api.archipad.com/';
    }
    return 'https://api.archipad.dev/';
}

function getTrackingServiceConfig() {
    const defaultConfig = {
        endpoint: "https://api-eu.mixpanel.com",
        projectToken: "88806b6b40b027d254d7931ddaa7816a",
        allowListEvent: [
            TrackingName.signin,
            TrackingName.first_observation,
            TrackingName.first_report,
            TrackingName.daily_activity,
            TrackingName.add_workpackage,
            TrackingName.add_project_member,
            TrackingName.logout,
        ],
    };
    if (BUILD_PROD) {
        return {
            ...defaultConfig,      
            projectToken: "2dcd3601f125fcf66b82512c1f3a3d95",
        };
    }
    return defaultConfig;
}

// NTH : Merge cloud-app/env with contractor-app/env
const config = {
    "sentry": getSentryConfig(`archipad-cloud@${BUILD_REVISION}`),
    "@core/services/locale": {
        supportedLocales: ['en', 'fr', 'es', 'de', 'it'],
    },
    "@core/services/config": {
        configPath: 'config',
        appVersion: '1.0',
        clientName: 'cloud',
        locale: 'en',
        hasReportGenerationService: true,
    },
    "@archipad/backend/project/dependenciesManager": {
        platform: "cloud",
    },

    "core/components/ui": {
        pixelMutiplier: 10.0,    // workaround for pixel rounding
    },

    "@archipad/report/report-generation-service": {
        reportGeneratorEnv: REPORT_GENERATOR_ENV,
	},
    "@archipad/oauth-client": getOAuthClientConfiguration(),
    "@archipad/admin-api-uri": getAdminApiURI(),
    "@archipad/archipad-api": {
        baseURL: getArchipadAPIBaseURL(),
    },
    "@archipad/tracking-service": getTrackingServiceConfig(),
    "@archipad/profile": {
        profileURI: getProfileURI(),
    },
    "featureFlags": {
        "plusAuthenticationServer": true,
        "planSegmentation": true,
    },
};

const stage = {
    /** @TOOL Google Analytics test account */
    // "GTAG_ID": "G-X44CRH8N6C",

    "@archipad/backend/rpc/bootstrap": {
        serverBaseUrl: getServerBaseURL(),
        optimizeCorsPreflight: false,
        contractorPath: 'https://stage-contractor.bigsool.io/',
    },
    "@archipad/backend/text-modifier": {
        serverBaseUrl: getTextModifierAPIBaseURL(),
    },
    "@archipad/backend/segmentation": {
        serverBaseUrl: getSegmentationAPIBaseURL(),
    },
    "@ui-archipad/backend/file-upload": {
        apiKey: "A5PFJScGyQb1CEPayM00pz",
        bucket: getBucket(),
    },
    "@ui-archipad/backend/archiweb/product": {
        apiKey: "pk_test_DQiAlW3v2J2Jb65foqjJCWRi",
        paymentMethodConfiguration: 'pmc_1T4kpGH98v6BvIEHwN91mDI5',
    },
    "@core/services/logger.service": {
        DEBUG: true, INFO: true, WARN: true, ERROR: true,

        // @TOOL : Avoid having too much messages as Chrome dev tools becomes really buggy after
        // classes: {
        //     orm: { DEBUG: false, INFO: false, WARN: false, ERROR: true, },
        //     queue: { DEBUG: false, INFO: true, WARN: true, ERROR: true, },
        //     tracking:  { DEBUG: false, INFO: true, WARN: true, ERROR: true, },
        //     index:  { DEBUG: false, INFO: false, WARN: true, ERROR: true, },
        // }
    },
    "@archipad/admin": {
        checkoutURI: "https://admin.archipad.dev/subscriptions/checkout",
        subscriptionsURI: "https://admin.archipad.dev/companies/me/subscriptions",
    },
};

const prod = {
    "GTAG_ID": "G-T4N992704N",

    "@archipad/backend/rpc/bootstrap": {
        serverBaseUrl: getServerBaseURL(),
        optimizeCorsPreflight: false,
        contractorPath: "https://archipad.mobi/",
    },
    "@archipad/backend/text-modifier": {
        serverBaseUrl: getTextModifierAPIBaseURL(),
    },
    "@archipad/backend/segmentation": {
        serverBaseUrl: getSegmentationAPIBaseURL(),
    },
    "@ui-archipad/backend/file-upload": {
        apiKey: "AJ1PcuabPQWP4sfcfckFVz",
        bucket: "archipad-services-fr",
    },
    "@ui-archipad/backend/archiweb/product": {
        apiKey: "pk_live_Le3JdOb6Cmv4DIXDYHzn7e7X",
        paymentMethodConfiguration: "pmc_1T4l7AH98v6BvIEHDbbdquGy",
    },
    "@archipad/admin": {
        checkoutURI: "https://admin.archipad.com/subscriptions/checkout",
        subscriptionsURI: "https://admin.archipad.com/companies/me/subscriptions",
    },
}
const envConfig = Object.assign({}, config, BUILD_PROD ? prod : stage);

export default envConfig;
